import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, Loader2, Search, UserPlus, X } from 'lucide-react';
import { Modal } from './Modal';
import { UserPickerRow } from './UserPickerRow';
import { ActiveCall, OnlineUser, User, handleAvatarError } from '../types';
import { resolveAvatarUrl } from '../utils/serverUrl';
import { IS_COARSE_POINTER } from '../utils/pointer';
import { useChannelMembers } from '../services/channelService';
import { useAffiliateUserSearch, SelectableUser } from '../hooks/useAffiliateUserSearch';
import { callController, describeInviteResult, inviteRoom } from '../services/callController';
import { useToast } from '../contexts/ToastContext';

/**
 * 통화 중 초대 창(2026-11-04).
 *  · '이 대화방 멤버' — 통화에 없는 방 멤버(거절·부재중 포함). 고르면 벨이 다시 울린다(그룹 대화방만)
 *  · '다른 사람'(1:1은 '초대할 사람') — 내 계열사 명부. 대화방에 넣지 않고 **이 통화에만** 들어오는 게스트다
 * 누가 멤버이고 누가 게스트인지는 서버(call:invite)가 다시 가른다 — 이 창의 구분은 안내용이다.
 * 고를 수 있는 수 = 정원 - 참가자 - 초대 벨(inviteRoom — 서버와 같은 셈).
 * ⚠ useAffiliateUserSearch의 excludeUserIds는 목록을 받을 때 한 번만 적용된다 — 통화 중에 바뀌는 참가자·벨은 렌더에서 거른다
 */
interface CallInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  call: ActiveCall;
  currentUser: User;
  onlineUsers: OnlineUser[];
}

const ringingBadge = (
  <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium">호출 중</span>
);

const CallInviteModal: React.FC<CallInviteModalProps> = ({ isOpen, onClose, call, currentUser, onlineUsers }) => {
  const { showToast } = useToast();
  const myId = String(currentUser.id);
  const isDm = call.channelType === 'dm';
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [inviting, setInviting] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { members, loading: membersLoading } = useChannelMembers(isOpen ? call.channelId : null);
  const {
    filteredUsers,
    allUsers,
    loading: directoryLoading,
    error: directoryError,
    searchQuery,
    setSearchQuery,
  } = useAffiliateUserSearch({
    isActive: isOpen,
    currentUserId: currentUser.id,
    // 게스트는 초대한 사람 계열사 명부에서만 부를 수 있다(서버가 같은 규칙으로 확인한다)
    defaultAffiliateId: currentUser.affiliateId,
  });

  const inCall = useMemo(() => new Set(call.participants.map(p => String(p.userId))), [call.participants]);
  const ringingIds = useMemo(() => new Set((call.ringing || []).map(r => String(r.userId))), [call.ringing]);
  const memberIds = useMemo(() => new Set(members.map(m => String(m.userId))), [members]);
  const room = inviteRoom(call);
  const max = call.maxParticipants || 6;

  // 고른 뒤 그 사람이 들어왔거나 벨이 울리기 시작했으면(다른 참가자가 먼저 불렀다) 고른 목록에서 뺀다
  useEffect(() => {
    setPicked(prev => {
      const next = new Set(Array.from(prev).filter(id => !inCall.has(id) && !ringingIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [inCall, ringingIds]);

  const q = searchQuery.trim().toLowerCase();

  // '이 대화방 멤버' — 통화에 없는 방 멤버(1:1은 상대가 통화에 있으니 비어 있다)
  const memberRows: SelectableUser[] = useMemo(() => {
    if (isDm) return [];
    const dirById = new Map(allUsers.map(u => [String(u.id), u]));
    return members
      .filter(m => String(m.userId) !== myId && !inCall.has(String(m.userId)))
      .filter(m => !q || m.userName.toLowerCase().includes(q) || (dirById.get(String(m.userId))?.depart || '').toLowerCase().includes(q))
      .map(m => {
        const dir = dirById.get(String(m.userId));
        return {
          id: m.userId,
          username: m.userName,
          avatar: m.avatar || dir?.avatar || '',
          status: 'offline' as const,
          depart: dir?.depart,
          selected: picked.has(String(m.userId)),
        };
      });
  }, [isDm, members, allUsers, myId, inCall, q, picked]);

  // '다른 사람' — 명부에서 방 멤버·통화 참가자·나를 뺀 사람(게스트)
  const guestGroups = useMemo(() => {
    const groups = new Map<string, SelectableUser[]>();
    filteredUsers.forEach(u => {
      const id = String(u.id);
      if (id === myId || inCall.has(id) || memberIds.has(id)) return;
      const dept = u.depart || '미배정';
      if (!groups.has(dept)) groups.set(dept, []);
      groups.get(dept)!.push({ ...u, selected: picked.has(id) });
    });
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredUsers, myId, inCall, memberIds, picked]);

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    allUsers.forEach(u => m.set(String(u.id), u.username));
    members.forEach(u => m.set(String(u.userId), u.userName));
    return m;
  }, [allUsers, members]);

  const pickedUsers = useMemo(() => Array.from(picked).map(id => {
    const dir = allUsers.find(u => String(u.id) === id);
    const mem = members.find(u => String(u.userId) === id);
    return { id, name: mem?.userName || dir?.username || id, avatar: mem?.avatar || dir?.avatar || '' };
  }), [picked, allUsers, members]);

  const toggle = (userId: string | number) => {
    const id = String(userId);
    if (ringingIds.has(id)) return; // 이미 벨이 울린다 — 다시 부를 수 없다
    const picking = !picked.has(id);
    if (picking && picked.size >= room) {
      showToast({ type: 'info', message: `지금은 ${room}명까지 초대할 수 있습니다(최대 ${max}명).` });
      return;
    }
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    // 검색 중에 사람을 고르면 검색어를 비운다(멤버 초대 창과 같은 규칙). 터치 기기는 키보드를 다시 올리지 않는다
    if (picking && searchQuery) {
      setSearchQuery('');
      if (!IS_COARSE_POINTER) searchInputRef.current?.focus();
    }
  };

  const handleInvite = async () => {
    if (picked.size === 0 || inviting) return;
    setInviting(true);
    try {
      const res = await callController.invite(Array.from(picked));
      describeInviteResult(res, id => nameById.get(id) || '선택한 사람').forEach(t => showToast(t));
      if (res.ok) onClose();
    } finally {
      setInviting(false);
    }
  };

  const listLoading = membersLoading || directoryLoading;
  const noRoom = room === 0;
  const hasAnyRow = memberRows.length > 0 || guestGroups.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="통화 초대"
      size="md"
      bodyFlex
      footer={
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-slate-500 min-w-0 truncate">
            {noRoom ? `통화 인원이 가득 찼습니다(최대 ${max}명)` : `지금 ${room}명까지 초대할 수 있습니다 · ${picked.size}명 선택`}
          </p>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={onClose}
              disabled={inviting}
              className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              취소
            </button>
            <button
              onClick={handleInvite}
              disabled={inviting || picked.size === 0}
              className="px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {inviting ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
              {inviting ? '초대 중…' : '초대'}
            </button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-3 flex-auto min-h-0">
        {/* 고른 사람 */}
        {pickedUsers.length > 0 && (
          <div className="flex flex-wrap gap-2 p-2.5 bg-slate-50 border border-slate-100 rounded-lg max-h-24 overflow-y-auto">
            {pickedUsers.map(u => (
              <span key={u.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-brand-100 text-brand-700 rounded-full text-sm">
                <img src={resolveAvatarUrl(u.avatar)} onError={handleAvatarError} alt="" className="w-4 h-4 rounded-full" />
                {u.name}
                <button
                  type="button"
                  onClick={() => toggle(u.id)}
                  className="hover:bg-brand-200 rounded-full p-0.5 transition-colors"
                  aria-label={`${u.name} 선택 해제`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            aria-label="이름이나 부서로 찾기"
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="이름이나 부서로 찾기"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400 transition-colors"
          />
        </div>

        {directoryError && (
          <div className="px-3 py-2 bg-brand-50 border border-brand-200 rounded-lg text-brand-700 text-sm">{directoryError}</div>
        )}

        <div className="border border-slate-200 rounded-lg overflow-hidden flex-auto min-h-0 flex flex-col">
          <div className="flex-auto min-h-0 overflow-y-auto">
            {listLoading ? (
              // 멤버 목록이 오기 전에 명부를 그리면 방 멤버가 잠깐 '게스트'로 보인다 — 둘 다 기다린다
              <div className="flex items-center justify-center py-8 text-slate-400">
                <Loader2 size={24} className="animate-spin mr-2" />
                불러오는 중...
              </div>
            ) : !hasAnyRow ? (
              <div className="flex flex-col items-center justify-center py-8 text-slate-400 text-sm text-center px-4">
                <UserPlus size={32} className="mb-2 opacity-50" />
                <p>{q ? '검색 결과가 없습니다' : '초대할 수 있는 사람이 없습니다'}</p>
                {!q && <p className="mt-1">모두 이미 통화에 들어와 있습니다</p>}
              </div>
            ) : (
              <>
                {memberRows.length > 0 && (
                  <section>
                    <div className="sticky top-0 z-[1] px-4 py-2 bg-white/95 backdrop-blur-sm border-b border-slate-100">
                      <p className="text-sm font-medium text-slate-700">이 대화방 멤버</p>
                      <p className="text-xs text-slate-500">벨이 다시 울립니다</p>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {memberRows.map(u => (
                        <UserPickerRow
                          key={u.id}
                          user={u}
                          onlineUsers={onlineUsers}
                          onToggle={toggle}
                          badge={ringingIds.has(String(u.id)) ? ringingBadge : undefined}
                        />
                      ))}
                    </div>
                  </section>
                )}
                {guestGroups.length > 0 && (
                  <section>
                    <div className="sticky top-0 z-[1] px-4 py-2 bg-white/95 backdrop-blur-sm border-y border-slate-100">
                      <p className="text-sm font-medium text-slate-700">{isDm ? '초대할 사람' : '다른 사람'}</p>
                      <p className="text-xs text-slate-500">대화방에는 추가되지 않고 이 통화에만 들어옵니다. 대화 내용은 볼 수 없습니다</p>
                    </div>
                    {guestGroups.map(([dept, users]) => (
                      <div key={dept}>
                        <div className="flex items-center gap-2 px-4 py-1.5 text-xs text-slate-400 bg-slate-50/60">
                          <Building2 size={12} />
                          {dept}
                          <span className="opacity-60">({users.length})</span>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {users.map(u => (
                            <UserPickerRow
                              key={u.id}
                              user={u}
                              onlineUsers={onlineUsers}
                              onToggle={toggle}
                              badge={ringingIds.has(String(u.id)) ? ringingBadge : undefined}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </section>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CallInviteModal;
